import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn, UserPlus, ArrowLeft, Check } from "lucide-react";
import { BrandBadge } from "@/components/BrandMark";
import TourTeaser from "@/components/tour/TourTeaser";
import PlatformTour, { trackTour } from "@/components/tour/PlatformTour";
import TourVideo from "@/components/tour/TourVideo";
import ErrorBoundary from "@/components/ErrorBoundary";
import PlanShowcase from "@/components/tour/PlanShowcase";
import { DemoVideo } from "@/components/DemoVideo";
import { SplashNav } from "@/components/splash/SplashNav";
import { FounderBlock } from "@/components/splash/FounderBlock";
import { PricingSection } from "@/components/splash/PricingSection";
import { FeatureSection } from "@/components/splash/FeatureSection";
import { ProofSection } from "@/components/splash/ProofSection";
import { ClosingSection } from "@/components/splash/ClosingSection";
import { api, formatApiErrorDetail } from "@/lib/api";
import TermsDialog from "@/components/TermsDialog";

/* What a company of this size actually pays: plan base + extra properties. Growing past the
   included count never forces a tier change, so the cheapest plan wins — 8 properties stays on
   Starter at $89 rather than being pushed to Professional at $199. */
const TIERS = [
  { max: 5, label: "Starter", base: 59, rate: 10 },
  { max: 25, label: "Professional", base: 199, rate: 8 },
  { max: 100, label: "Business", base: 499, rate: 5 },
];

const planPreview = (count) => {
  const n = Math.max(Number(count) || 0, 0);
  const best = TIERS
    .map((t) => ({ ...t, total: t.base + Math.max(n - t.max, 0) * t.rate }))
    .sort((a, b) => a.total - b.total)[0];
  const extra = Math.max(n - best.max, 0);
  const detail = extra
    ? ` — $${best.base} for ${best.max} properties plus ${extra} × $${best.rate} = $${best.total}/month`
    : ` — $${best.total}/month`;
  return `Your size puts you on ${best.label}${detail}, starting the day your 14-day trial ends.`;
};

/* Who gets the rendered film instead of the live animation engine.
   The engine is a React scene graph driving a dozen full-size product screenshots at 60fps; a
   phone — installed app or browser — cannot hold that without judder, and on an installed iOS app
   it also ran into the WKWebView memory ceiling. Anything touch-driven or narrow gets the film,
   which is the same picture with the captions in it and is decoded by the hardware. */
const prefersFilm = () => {
  if (typeof window === "undefined") return false;
  const mq = (q) => window.matchMedia?.(q).matches === true;
  return mq("(display-mode: standalone)") || mq("(display-mode: fullscreen)")
    || window.navigator.standalone === true
    || mq("(pointer: coarse)")
    || window.innerWidth < 1024
    || /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent || "");
};

const TourPlayer = (props) => (prefersFilm() ? <TourVideo {...props} /> : <PlatformTour {...props} />);

export default function Login() {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(
    // Prospects arriving from the shared tour page (/tour/) land straight on signup.
    new URLSearchParams(window.location.search).get("start") === "trial" ? "signup" : "splash");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "",
    company_name: "", property_count: "", team_size: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const formRef = useRef(null);

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSentTo(email);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  // The 3-minute platform tour is no longer offered on the splash: the 40-second demo replaces it,
  // so the page carries one video, not two. The player below stays wired for the /tour/ link.
  const startFromTour = () => {
    trackTour("cta_start");
    setTourOpen(false);
    setMode("signup");
  };
  const exploreFromTour = () => {
    trackTour("cta_explore");
    setTourOpen(false);
    setMode("plans");
  };

  // Every trial button on the long page does the same thing: take them to the form.
  const startTrial = () => {
    trackTour("signup_initiated");
    setMode("signup");
  };

  // On a phone the brand panel stacks above the form and is taller than the viewport, so scrolling
  // to the top of the document leaves the form off-screen. Scroll to the panel itself instead.
  useEffect(() => {
    if (mode === "splash") return;
    requestAnimationFrame(() =>
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [mode]);

  const submitLogin = async (e) => {    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await signup({
      ...form,
      property_count: Number(form.property_count) || 0,
      team_size: Number(form.team_size) || 0,
    });
    if (!res.ok) {
      setBusy(false);
      setError(res.error);
      return;
    }
    trackTour("signup_completed");
    setBusy(false);
    // Terms must be accepted and recorded before any Stripe checkout opens.
    setTermsOpen(true);
  };

  const startTrialAfterTerms = async () => {
    setTermsOpen(false);
    setBusy(true);
    setError("");
    // Card on file before the trial starts: Stripe holds the card, charges nothing for 14 days.
    try {
      const { data } = await api.post("/plans/start-trial",
        { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
      return;
    } catch (err) {
      setBusy(false);
      setError(formatApiErrorDetail(err.response?.data?.detail)
        || "Account created, but we could not open the secure card form. Open Settings to finish.");
      setTimeout(() => navigate("/dashboard"), 2500);
    }
  };

  return (
    <div data-testid="splash-page">
      {mode === "splash" && <SplashNav onLogin={() => setMode("login")} />}
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]" data-testid="auth-page">
      <div
        data-testid="brand-panel"
        className="relative bg-royal-deep sidebar-grain flex flex-col justify-between gap-8 sm:gap-10 p-7 sm:p-10 lg:p-12 lg:min-h-screen"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 3.25rem)" }}
      >
        <div className="flex items-center gap-3">
          <BrandBadge size="h-11 w-11" icon="h-8 w-8" />
          <div>
            <p className="font-display text-white text-lg sm:text-xl font-semibold leading-none">Cornerstone Ops</p>
            <p className="text-gold text-[10px] sm:text-[11px] tracking-[0.22em] uppercase mt-1">The Operational Command Center</p>
            <p
              className="text-white/90 text-[10px] sm:text-[11px] tracking-[0.18em] uppercase font-semibold mt-1.5"
              data-testid="brand-slogan"
            >
              One Operation. One Cornerstone.
            </p>
          </div>
        </div>
        <div className="max-w-md">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold" data-testid="hero-eyebrow">
            For Property-Service Companies
          </p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1] mt-3">
            Your entire operation.
            <span className="block text-gold">Finally connected.</span>
          </h1>
          <p className="text-blue-100/70 mt-5 lg:mt-6 text-sm sm:text-base leading-relaxed">
            Cornerstone Ops brings scheduling, properties, teams, inspections, issues, vendors,
            payments and operational workflows into one powerful platform.
          </p>
          <FounderBlock />
          <p className="text-blue-200/50 text-[11px] sm:text-xs mt-5">
            A Christian-Family Owned Company - Outer Banks North Carolina
          </p>
          <div className="h-px gold-rule mt-8 lg:mt-10 mb-6" />
          <p className="text-gold text-[11px] tracking-[0.18em] uppercase font-semibold">Psalm 24:1, NLT</p>
          <p className="text-blue-100/80 text-sm italic mt-2">
            "The earth is the LORD'S, and everything in it."
          </p>
          <div className="mt-6 space-y-2.5" data-testid="stewardship-slogan">
            {["God owns it.", "We steward it.", "We protect it."].map((line) => (
              <p
                key={line}
                className="font-display text-white text-xs sm:text-sm font-semibold uppercase leading-relaxed"
                style={{ letterSpacing: "0.2em", wordSpacing: "0.35em" }}
              >
                {line}
              </p>
            ))}
          </div>
        </div>
        <p className="text-blue-200/40 text-[11px] sm:text-xs">A Christian-Family Owned Company - Outer Banks North Carolina</p>
      </div>

      <div ref={formRef} className="flex items-center justify-center p-6 sm:p-10 lg:p-12 bg-background scroll-mt-16">
        <div className="w-full max-w-sm rise">

          {mode === "splash" && (
            <div data-testid="splash-panel">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-royal-deep leading-tight">
                The operational command center for property-service companies
              </h2>
              <p className="text-sm text-muted-foreground mt-2 mb-6">
                See the real product in 40 seconds, then start your trial.
              </p>
              <DemoVideo
                showCta={false}
                onPlay={() => trackTour("teaser_view")}
                onStart={() => { trackTour("signup_initiated"); setMode("signup"); }}
              />
              <Button
                data-testid="splash-trial-btn"
                onClick={() => { trackTour("signup_initiated"); setMode("signup"); }}
                className="mt-3 w-full h-12 bg-gold hover:bg-gold/90 text-royal-deep font-semibold text-base touch-manipulation transition-colors duration-200"
              >
                <UserPlus className="h-4 w-4 mr-2" /> Start Your 14-Day Free Trial
              </Button>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="splash-trial-terms">
                14-day free trial. Card on file, no charge until day 14. Cancel anytime.
              </p>
              <Button
                data-testid="splash-login-btn"
                onClick={() => setMode("login")}
                variant="outline"
                className="mt-3 w-full h-12 border-border text-royal-deep hover:bg-secondary font-semibold text-base touch-manipulation transition-colors duration-200"
              >
                <LogIn className="h-4 w-4 mr-2" /> Log In Access
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">
                Cleaners: your administrator creates your account — use Log In Access.
              </p>
            </div>
          )}

          {mode === "plans" && (
            <PlanShowcase
              onBack={() => setMode("splash")}
              onStart={() => { trackTour("signup_initiated"); setMode("signup"); }}
            />
          )}

          {mode === "login" && (
            <form onSubmit={submitLogin} data-testid="login-form">
              <button type="button" data-testid="back-to-splash-login" onClick={() => setMode("splash")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-royal hover:text-gold min-h-[40px] touch-manipulation transition-colors duration-200">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
              <h2 className="font-display text-3xl font-bold text-royal-deep mt-2">Welcome back</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-8">Sign in to manage your properties and crews.</p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input id="email" data-testid="login-email-input" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@cornerstoneops.com" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                  <Input id="password" data-testid="login-password-input" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 h-11" />
                </div>
              </div>

              {error && <p data-testid="login-error" className="mt-4 text-sm text-destructive">{error}</p>}

              <Button data-testid="login-submit-btn" type="submit" disabled={busy}
                className="mt-6 w-full h-12 bg-royal hover:bg-royal-deep text-white font-semibold transition-colors duration-200">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
              <button type="button" data-testid="login-forgot-password-link"
                onClick={() => { setError(""); setSentTo(""); setMode("forgot"); }}
                className="mt-4 w-full text-xs font-semibold text-royal hover:text-gold min-h-[40px] touch-manipulation transition-colors duration-200">
                Forgot your password?
              </button>
              <button type="button" data-testid="switch-to-signup" onClick={() => setMode("signup")}
                className="mt-1 w-full text-xs font-semibold text-royal hover:text-gold min-h-[40px] touch-manipulation transition-colors duration-200">
                New here? Sign Up Access
              </button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={submitForgot} data-testid="forgot-password-form">
              <button type="button" data-testid="back-to-login-forgot" onClick={() => setMode("login")}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-royal hover:text-gold min-h-[40px] touch-manipulation transition-colors duration-200">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
              <h2 className="font-display text-3xl font-bold text-royal-deep mt-2">Reset your password</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-8">
                Enter the email you sign in with and we'll send a link that works once and expires
                in an hour.
              </p>
              {sentTo ? (
                <p data-testid="forgot-password-sent" className="rounded-lg bg-royal/5 p-4 text-sm text-royal-deep">
                  If <strong>{sentTo}</strong> has an account, the reset link is on its way. Check
                  your spam folder if it hasn't arrived in a couple of minutes.
                </p>
              ) : (
                <>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                    <Input data-testid="forgot-password-email-input" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)} placeholder="you@cornerstoneops.com"
                      className="mt-1.5 h-11" />
                  </div>
                  {error && <p data-testid="forgot-password-error" className="mt-4 text-sm text-destructive">{error}</p>}
                  <Button data-testid="forgot-password-submit-btn" type="submit" disabled={busy}
                    className="mt-6 w-full h-12 bg-royal hover:bg-royal-deep text-white font-semibold transition-colors duration-200">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                  </Button>
                </>
              )}
            </form>
          )}

          {mode === "signup" && (
            <form onSubmit={submitSignup} data-testid="signup-form">
              <button type="button" data-testid="back-to-splash-signup" onClick={() => setMode("splash")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-royal hover:text-gold min-h-[40px] touch-manipulation transition-colors duration-200">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
              <h2 className="font-display text-3xl font-bold text-royal-deep mt-2">Create your company</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-8">
                14 days free. Add your company details and a card to start — nothing is charged today,
                and your plan is priced from the size of your operation.
              </p>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company name</Label>
                  <Input data-testid="signup-company-input" required value={form.company_name}
                    onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                    placeholder="e.g. Coastal Property Care LLC" className="mt-1.5 h-11" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Properties</Label>
                    <Input data-testid="signup-properties-input" type="number" min={1} required
                      value={form.property_count}
                      onChange={(e) => setForm((f) => ({ ...f, property_count: e.target.value }))}
                      placeholder="e.g. 12" className="mt-1.5 h-11" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Team size</Label>
                    <Input data-testid="signup-team-input" type="number" min={1} required
                      value={form.team_size}
                      onChange={(e) => setForm((f) => ({ ...f, team_size: e.target.value }))}
                      placeholder="e.g. 6" className="mt-1.5 h-11" />
                  </div>
                </div>
                {Number(form.property_count) > 0 && (
                  <p data-testid="signup-plan-preview" className="text-[11px] text-royal-deep bg-secondary/60
                                                                  rounded-md px-3 py-2">
                    {planPreview(Number(form.property_count))}
                  </p>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
                  <Input data-testid="signup-name-input" required value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input data-testid="signup-email-input" type="email" required value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
                  <Input data-testid="signup-phone-input" value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                  <Input data-testid="signup-password-input" type="password" required value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="mt-1.5 h-11" />
                  <p className="mt-1.5 text-[11px] text-muted-foreground">At least 8 characters, with letters and numbers.</p>
                </div>
              </div>

              {error && <p data-testid="signup-error" className="mt-4 text-sm text-destructive">{error}</p>}

              <Button data-testid="signup-submit-btn" type="submit" disabled={busy}
                className="mt-6 w-full h-12 bg-gold hover:bg-gold/90 text-royal-deep font-semibold transition-colors duration-200">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to secure card setup"}
              </Button>
              <p className="mt-2 text-[11px] text-muted-foreground text-center">
                Card details are handled by Stripe. $0 today · your first payment is taken the day the
                14-day trial ends, then monthly on that date.
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground text-center">
                You'll review and accept our{" "}
                <a href="/terms" target="_blank" rel="noreferrer" data-testid="signup-terms-link"
                   className="font-semibold text-royal underline">Terms of Service</a> and{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" data-testid="signup-privacy-link"
                   className="font-semibold text-royal underline">Privacy Policy</a> before checkout.
              </p>
              <button type="button" data-testid="switch-to-login" onClick={() => setMode("login")}
                className="mt-4 w-full text-xs font-semibold text-royal hover:text-gold min-h-[40px] touch-manipulation transition-colors duration-200">
                Already have an account? Log In Access
              </button>
            </form>
          )}
        </div>
      </div>

      <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)}
        onAccepted={startTrialAfterTerms} context="signup" />
      {tourOpen && (
        <ErrorBoundary
          fallback={(retry) => (
            <div data-testid="tour-crash-card"
                 className="fixed inset-0 z-50 flex items-center justify-center bg-royal-deep px-6 text-center">
              <div>
                <p className="font-display text-xl font-semibold text-white">
                  The tour hit a snag
                </p>
                <p className="mt-3 max-w-md text-sm text-blue-100/70">
                  Nothing is broken — pick it back up, or close this and carry on.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <button data-testid="tour-crash-retry" onClick={retry}
                    className="h-11 rounded-full bg-gold px-7 font-display text-xs font-bold
                               uppercase tracking-[0.16em] text-royal-deep">
                    Resume the tour
                  </button>
                  <button data-testid="tour-crash-close" onClick={() => setTourOpen(false)}
                    className="h-11 rounded-full border border-white/25 px-7 font-display text-xs
                               font-bold uppercase tracking-[0.16em] text-white">
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        >
          <TourPlayer
            onClose={() => setTourOpen(false)}
            onStart={startFromTour}
            onExplore={exploreFromTour}
          />
        </ErrorBoundary>
      )}
    </div>

      {mode === "splash" && (
        <>
          <PricingSection onStart={startTrial} />
          <FeatureSection />
          <ProofSection />
          <ClosingSection onStart={startTrial} />
        </>
      )}
    </div>
  );
}
